import { Header } from "../../../components/layout/header";
import { LogOut, Plus, Search, Pencil, Trash, UserPlus, PlusCircle } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { useAuthStore } from "../../../stores/auth";
import { Main } from "../../../components/layout/main";
import { Input } from "../../../components/ui/input";
import { Checkbox } from "../../../components/ui/checkbox";
import { useProjects } from "../../../hooks/project";
import { useLocation, Outlet } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import TableCustomer from "../../../components/table";
import { useState } from "react";
import { deleteProject } from "../../../api/project";
import toastifyInstance from "../../../utils/toastify";
import { useQueryClient } from "@tanstack/react-query";

const Project: React.FC = () => {
    const { logout } = useAuthStore();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [selected, setSelected] = useState<string[]>([]);

    const { projects, isLoading, handleSetQuery, register, handleSubmit, onSubmit } = useProjects();

    const isUpdatePage = useLocation().pathname.includes("update-project");
    const isCreatePage = useLocation().pathname.includes("create-project");
    const isAddMembersPage = useLocation().pathname.includes("add-members");

    const handleDeleteProject = async (id: string) => {
        try {
            await deleteProject(id);
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            toastifyInstance.success("Project deleted successfully");
        } catch (error) {
            toastifyInstance.error("Project deleted failed");
        }
    };

    const columns = [
        { label: "Name", value: "name" },
        { label: "Description", value: "description" },
        { label: "Status", value: "status", isStatus: true },
        {
            label: "Start Date", value: "startDate",
            render: ({ data }: { data: any }) => {
                return (
                    <p className="text-sm text-gray-500">
                        {new Date(data.startDate).toLocaleDateString()}
                    </p>
                )
            }
        },
        {
            label: "End Date", value: "endDate",
            render: ({ data }: { data: any }) => {
                return (
                    <p className="text-sm text-gray-500">
                        {data.endDate ? new Date(data.endDate).toLocaleDateString() : "N/A"}
                    </p>
                )
            }
        },
        {
            label: "Update",
            value: "update",
            render: ({ data }: { data: any }) => {
                console.log(data);
                return (<Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/projects/update-project?id=${data._id}`)}
                >
                    <Pencil className="h-4 w-4" />
                </Button >)
            }
        },
        {
            label: "Delete",
            value: "delete",
            render: ({ data }: { data: any }) => (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteProject(data._id)}
                >
                    <Trash className="h-4 w-4" />
                </Button>
            )
        },
        {
            label: "Add Member",
            value: "addMember",
            render: ({ data }: { data: any }) => (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/projects/add-members?id=${data._id}`)}
                >
                    <UserPlus className="h-4 w-4" />
                </Button>
            )
        },
        {
            label: "Add Task",
            value: "addTask",
            render: ({ data }: { data: any }) => (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/tasks/create-task`)}
                >
                    <PlusCircle className="h-4 w-4" />
                </Button>
            )
        }
    ];

    const handleSelect = (id: string) => {
        setSelected(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            }
            return [...prev, id];
        });
    };

    const handleSelectAll = () => {
        if (selected.length === projects?.length) {
            setSelected([]);
        } else {
            setSelected(projects?.map(project => project._id).filter((id): id is string => id !== undefined) || []);
        }
    };

    return (
        <>
            <Header fixed className='flex justify-between w-[inherit]'>
                <div className='mr-0 flex items-center justify-end space-x-4 w-[inherit]'>
                    <Button variant="outline" size="icon" onClick={logout}>
                        <LogOut />
                    </Button>
                </div>
            </Header>

            <Main>
                <h4 className="text-xl font-bold">
                    <span className="hover:underline cursor-pointer" onClick={() => navigate(-1)}>Project</span>
                    {isCreatePage ? " / Create" : isUpdatePage ? " / Update" : isAddMembersPage ? " / Add Members" : ""}
                </h4>

                <Outlet />

                {!isUpdatePage && !isCreatePage && !isAddMembersPage &&
                    <>
                        <div className="flex mt-4 flex-wrap items-center gap-4 justify-between">
                            <div className="flex items-center gap-2">
                                <form onSubmit={handleSubmit(onSubmit)} className="max-w-[170px] flex items-center gap-2 mr-3">
                                    <Input placeholder="Search" {...register("search")} />
                                    <Button variant="outline" type="submit" size="icon">
                                        <Search />
                                    </Button>
                                </form>

                                <div className="flex flex-col gap-4">
                                    <div className="flex items-center gap-2 bg-slate-100 px-2 py-2 rounded-md">
                                        <Checkbox defaultChecked={true} className="border-black" onCheckedChange={(checked) => {
                                            handleSetQuery({ status: !!checked });
                                        }} />
                                        <p className="text-sm font-semibold">active</p>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={() => navigate('/projects/create-project')}>
                                    <Plus />
                                </Button>
                            </div>
                        </div>

                        <TableCustomer
                            columns={columns}
                            data={projects || []}
                            loading={isLoading}
                            selected={selected}
                            handleSelect={handleSelect}
                            handleSelectAll={handleSelectAll}
                        />
                    </>
                }
            </Main>
        </>
    )
}

export default Project;